import { SpecialProjectOutput } from "@serializers/special-projects"
import { makeObservable, observable, action, computed } from "mobx"
import { addDays } from "date-fns"
import { getCookie, setCookies } from "cookies-next"
import { Context, Languages } from "@providers"
import { push } from "@lib/history"
import { stringify, parse } from "query-string"
import { isServer } from "@lib/isomorphic"
import { htmlToTextTransform } from "@lib/html"
import { FEATURE_TOGGLES_PRESET } from "@serializers/feature-toggles"
import { SMART_BANNER_PRESET } from "@serializers/smart-banner"
import { COOKIES_KEYS } from "@lib/cookies"
import { config } from "@lib/config"

import { BaseModel } from "./base.model"

import type { NextPageContext } from "next"
import type { MouseEventHandler } from "react"
import type { LanguageISO, Redirect, PressLink } from "@providers"
import type { ConfigData } from "@lib/config"
import type { FeatureTogglesSerializerOutput } from "@serializers/feature-toggles"
import type { Localization } from "@serializers/localization"
import type { DocumentSerializerOutput } from "@serializers/documents"
import type { SmartBannerSerializerOutput } from "@serializers/smart-banner"
import type { BranchLinksGlobal } from "@serializers/branch-links"

const BRANCH_LINKS_DEFAULT: BranchLinksGlobal = Object.freeze({
  ru: {},
  en: {},
  fr: {},
  de: {},
  zh: {},
})

const PRESS_LINK_DEFAULT: PressLink = {
  enabled: false,
  url: "https://press.pure.app/",
}

export class GlobalModel extends BaseModel {
  // @ts-ignore
  constructor(params) {
    super(params)
    makeObservable(this)
  }

  LOCAL_STORAGE_GPDR_KEY = "pure.landing.cookies.accepted"

  appleAuthWindow: Window | null = null

  async postInit() {
    window.addEventListener("message", (event) => {
      if (
        event.origin === "https://appleid.apple.com" &&
        this.appleAuthWindow
      ) {
        const { data } = JSON.parse(event.data)
        this.appleAuthWindow.close()
        this.handleAppleAuthSuccess(data)
      }
    })
  }

  @computed get langShortname() {
    const key = Object.keys(Languages).find((key) => key === this.lang) ?? "en"
    // @ts-ignore
    return Languages[key].shortname
  }

  @observable isMenuOpened = false

  openMenu: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation()
    this.applyData({ isMenuOpened: true })
  }

  closeMenu = () => {
    this.applyData({ isMenuOpened: false })
  }

  toggleMenu = () => {
    this.applyData({ isMenuOpened: !this.isMenuOpened })
  }

  @observable isSmartBannerOpened = true
  @observable isSmartBannerDisabled = false

  initSmartBanner = (ctx?: NextPageContext) => {
    try {
      if (ctx) {
        const search = ctx.req?.url?.split("?").slice(-1)[0] || ""
        const utmSource = parse(search).utm_source
        const fromApp = utmSource === "inapp"
        let smartBannerDisabledCookie = false

        if (fromApp) {
          this.setSmartBannerDisabledCookie(ctx)
        } else {
          smartBannerDisabledCookie = Boolean(
            getCookie(COOKIES_KEYS["pure.journal.smart_banner.disabled"], {
              req: ctx.req,
            }),
          )
        }

        this.applyData({
          isSmartBannerDisabled: smartBannerDisabledCookie || fromApp,
        })
      }
    } catch (e) {
      /* eslint-disable-next-line no-console */
      console.error("Init smart banner error", e)
    }
  }

  setIsSmartBannerDisabled = (isDisabled: boolean) => {
    this.applyData({ isSmartBannerDisabled: isDisabled })
  }

  closeSmartBanner = () => {
    this.applyData({ isSmartBannerOpened: false })
  }

  openSmartBanner = () => {
    this.applyData({ isSmartBannerOpened: true })
  }

  setSmartBannerDisabledCookie = (ctx?: NextPageContext) => {
    const redisplayInterval = this.isPureDating
      ? this.featureToggles.smartBanner?.redisplayIntervalDating
      : this.featureToggles.smartBanner?.redisplayIntervalJournal

    setCookies(COOKIES_KEYS["pure.journal.smart_banner.disabled"], 1, {
      expires: addDays(new Date(), redisplayInterval || 30),
      req: ctx?.req,
      res: ctx?.res,
    })
  }

  @observable lang = "ru" as LanguageISO
  @observable accessLanguages = [] as LanguageISO[]
  @observable config!: ConfigData
  @observable
  featureToggles = FEATURE_TOGGLES_PRESET as FeatureTogglesSerializerOutput

  @observable pages = {
    aboutUs: {
      enabled: false,
    },
    faq: {
      enabled: false,
    },
    terms: {
      enabled: false,
    },
  }
  @observable templates = [] as Localization[]

  @observable htmlEmbeddedList = {} as { [uniqId: string]: string }

  @observable firstServerUrl = ""

  @observable originUrl = ""

  @observable hasAnyOneOfPureDatingHTTPHeader = false
  @observable hasAnyOneOfPureAppRuHTTPHeader = false

  @observable redirects = [] as Redirect[]

  @observable pressLink = PRESS_LINK_DEFAULT as PressLink

  @observable terms = [] as DocumentSerializerOutput[]
  getTerms = async () => {
    try {
      const terms = await this.Providers.GlobalProvider.getTerms(
        this.Models.GlobalModel.lang,
      )
      this.applyData({ terms })
    } catch {}
  }

  @observable privacy = [] as DocumentSerializerOutput[]
  getPrivacy = async () => {
    try {
      const privacy = await this.Providers.GlobalProvider.getPrivacy(
        this.Models.GlobalModel.lang,
      )
      this.applyData({ privacy })
    } catch {}
  }

  @observable security = [] as DocumentSerializerOutput[]
  getSecurity = async () => {
    try {
      const security = await this.Providers.GlobalProvider.getSecurity(
        this.Models.GlobalModel.lang,
      )
      this.applyData({ security })
    } catch {}
  }

  @observable guidelines = [] as DocumentSerializerOutput[]
  getGuidelines = async () => {
    try {
      const guidelines = await this.Providers.GlobalProvider.getGuideline(
        this.Models.GlobalModel.lang,
      )
      this.applyData({ guidelines })
    } catch {}
  }

  @observable smartBanner = SMART_BANNER_PRESET as SmartBannerSerializerOutput
  getSmartBanner = async () => {
    try {
      const smartBanner = await this.Providers.GlobalProvider.getSmartBanner(
        this.Models.GlobalModel.lang,
      )
      this.applyData({ smartBanner })
    } catch {}
  }

  @computed get isPureDating() {
    if (isServer()) {
      return (
        this.firstServerUrl.includes(PURE_DATING_DOMAIN) ||
        this.hasAnyOneOfPureDatingHTTPHeader
      )
    }

    return (
      window.location.host.includes(PURE_DATING_DOMAIN) ||
      this.hasAnyOneOfPureDatingHTTPHeader
    )
  }

  @computed get isPureAppRu() {
    if (isServer()) {
      return (
        this.firstServerUrl.includes(PURE_APP_RU_DOMAIN) ||
        this.hasAnyOneOfPureAppRuHTTPHeader
      )
    }

    return (
      window.location.host.includes(PURE_APP_RU_DOMAIN) ||
      this.hasAnyOneOfPureAppRuHTTPHeader
    )
  }

  doHeadersContainDomain = (ctx: NextPageContext, domain: string) => {
    return (
      ctx.req?.headers.origin?.includes(domain) ||
      ctx.req?.headers.host?.includes(domain) ||
      ctx.req?.headers[":authority:"]?.includes(domain) ||
      ctx.req?.headers["X-Pure-Origin"]?.includes(domain) ||
      ctx.req?.headers["x-pure-origin"]?.includes(domain)
    )
  }

  setFirstServerUrl = action((ctx: NextPageContext) => {
    const protocol = "https:"
    const host = ctx.req?.headers.host ?? ""
    const path = `${this.config.prefix}${ctx.req?.url ?? ""}`
    this.originUrl = this.generateSiteUrl({
      protocol,
      host,
      path: this.config.prefix,
    })
    this.firstServerUrl = this.generateSiteUrl({ protocol, host, path })
    const hasAnyOneOfPureDatingHTTPHeader = this.doHeadersContainDomain(
      ctx,
      PURE_DATING_DOMAIN,
    )
    const hasAnyOneOfPureAppRuHTTPHeader = this.doHeadersContainDomain(
      ctx,
      PURE_APP_RU_DOMAIN,
    )
    this.applyData({
      hasAnyOneOfPureDatingHTTPHeader,
      hasAnyOneOfPureAppRuHTTPHeader,
    })
  })

  setPostFirstServerUrl = ({
    slug,
    lang,
    isAmp,
  }: {
    slug: string
    lang: string
    isAmp: boolean
  }) => {
    const protocol = "https:"
    const host = config.data.host
    const prefix = config.data.prefix
    const path = `${prefix || ""}/${lang}/journal/${slug}`
    const search = isAmp ? "?amp=1" : ""

    this.applyData({
      firstServerUrl: this.generateSiteUrl({ protocol, host, path, search }),
    })

    console.log("FIRST SERVER URL", isServer(), this.firstServerUrl) // eslint-disable-line
  }

  generateSiteUrl = ({
    protocol,
    host,
    path,
    search = "",
  }: {
    protocol: string
    host: string
    path: string
    search?: string
  }) => {
    return `${protocol}//${host}${path}${search}`
  }

  @computed get currentUrl() {
    if (isServer()) return this.firstServerUrl
    else
      return this.generateSiteUrl({
        protocol: window.location.protocol,
        host: window.location.host,
        path: window.location.pathname,
      })
  }

  @observable specialProjects: SpecialProjectOutput[] = []
  @computed get specProjectsByLang() {
    return this.specialProjects.filter(({ lang }) => lang === this.lang)
  }
  getSpecialProjects = async () => {
    const specialProjects = await this.Providers.GlobalProvider.getSpecialProjects()

    this.applyData({ specialProjects })
  }

  @observable branchLinks: BranchLinksGlobal | {} = BRANCH_LINKS_DEFAULT
  @computed get branch_links() {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return this.branchLinks[this.lang] || BRANCH_LINKS_DEFAULT
  }

  @observable publishedVacanciesCounter = 0
  @computed get existPublishedVacancies() {
    return this.publishedVacanciesCounter > 0
  }

  getInitial = async () => {
    const {
      languages,
      pages,
      templates,
      redirects,
      featureToggles,
      specialProjects,
      branchLinks,
      publishedVacanciesCounter,
      pressLink,
    } = await this.Providers.GlobalProvider.getGlobal()
    this.applyData({
      accessLanguages: this.isPureDating ? ["en"] : languages,
      pages,
      templates,
      redirects,
      featureToggles,
      specialProjects,
      branchLinks,
      publishedVacanciesCounter,
      pressLink: pressLink || PRESS_LINK_DEFAULT,
    })
  }

  hydrate = (data: unknown) => {
    // @ts-ignore
    if (data?.lang) {
      // @ts-ignore
      this.applyData({ lang: data?.lang })
    }
    // @ts-ignore
    if (data?.accessLanguages)
      // @ts-ignore
      this.applyData({ accessLanguages: data?.accessLanguages })
    // @ts-ignore
    if (data?.config) {
      // @ts-ignore
      this.applyData({ config: data?.config })
    }
    // @ts-ignore
    if (data?.featureToggles) {
      // @ts-ignore
      this.applyData({ featureToggles: data?.featureToggles })
    }
    // @ts-ignore
    if (data?.smartBanner) {
      // @ts-ignore
      this.applyData({ smartBanner: data?.smartBanner })
    }
    // @ts-ignore
    if (data?.pages) {
      // @ts-ignore
      this.applyData({ pages: data?.pages })
    }
    // @ts-ignore
    this.applyData({ firstServerUrl: data?.firstServerUrl })
    // @ts-ignore
    this.applyData({ originUrl: data?.originUrl })
    // @ts-ignore
    if (data?.templates) this.applyData({ templates: data?.templates })
    // @ts-ignore
    if (data?.branchLinks) this.applyData({ branchLinks: data?.branchLinks })
    // @ts-ignore
    this.applyData({ terms: data?.terms })
    // @ts-ignore
    this.applyData({ privacy: data?.privacy })
    // @ts-ignore
    this.applyData({ security: data?.security })
    // @ts-ignore
    this.applyData({ guidelines: data?.guidelines })
    this.applyData({
      // @ts-ignore
      hasAnyOneOfPureDatingHTTPHeader: data?.hasAnyOneOfPureDatingHTTPHeader,
    })
    // @ts-ignore
    this.applyData({ isSmartBannerOpened: data?.isSmartBannerOpened })
    // @ts-ignore
    this.applyData({ isSmartBannerDisabled: data?.isSmartBannerDisabled })
    // @ts-ignore
    this.applyData({ specialProjects: data?.specialProjects })
    this.applyData({
      // @ts-ignore
      publishedVacanciesCounter: data?.publishedVacanciesCounter,
    })
    this.applyData({
      // @ts-ignore
      countryCode: data?.countryCode,
    })

    this.applyData({
      //  @ts-ignore
      pressLink: data?.pressLink,
    })
  }

  serialize = () => {
    return {
      hasAnyOneOfPureDatingHTTPHeader: this.hasAnyOneOfPureDatingHTTPHeader,
      lang: this.lang,
      accessLanguages: this.accessLanguages,
      config: this.config,
      featureToggles: this.featureToggles,
      pages: this.pages,
      firstServerUrl: this.firstServerUrl,
      originUrl: this.originUrl,
      templates: this.templates,
      branchLinks: this.branchLinks,
      terms: this.terms,
      privacy: this.privacy,
      security: this.security,
      guidelines: this.guidelines,
      smartBanner: this.smartBanner,
      isSmartBannerOpened: this.isSmartBannerOpened,
      isSmartBannerDisabled: this.isSmartBannerDisabled,
      specialProjects: this.specialProjects,
      publishedVacanciesCounter: this.publishedVacanciesCounter,
      countryCode: this.countryCode,
      pressLink: this.pressLink,
    }
  }

  setLang = async (lang: LanguageISO, force?: boolean) => {
    if (
      !force &&
      (this.accessLanguages.findIndex((accessLang) => accessLang === lang) ===
        -1 ||
        typeof Languages[lang] === "undefined")
    ) {
      return
    }

    this.applyData({ lang })
  }

  setConfig = action((config: ConfigData) => {
    this.config = config
  })

  changeLang = async (lang: LanguageISO) => {
    let currentUri = `${window.location.pathname}${window.location.search}`
    currentUri = currentUri.replace(
      new RegExp(`^${this.config.prefix}`, "ig"),
      "",
    )

    Object.values(Languages).forEach((lang) => {
      currentUri = currentUri.replace(new RegExp(`/${lang.id}$`, "ig"), "")
      currentUri = currentUri.replace(new RegExp(`/${lang.id}/`, "ig"), "")
    })

    await push({
      href: `/${lang}${
        currentUri.substring(0, 1) === "/" || currentUri === ""
          ? currentUri
          : `/${currentUri}`
      }`,
    })
  }

  getTemplate = (key: string, simpleText = true) => {
    // @ts-ignore
    const currentTemplates = this.templates[this.lang] || []
    const find = currentTemplates.find((template) => template.key === key)
    if (!find) return ""

    return simpleText ? htmlToTextTransform(find.template) : find.template
  }

  setHtmlEmbedded = action((uniqId: string, source: string) => {
    this.htmlEmbeddedList[uniqId] = source
  })

  openAppleAuth = () => {
    this.appleAuthWindow = window.open(
      `https://appleid.apple.com/auth/authorize?${stringify({
        client_id: "org.getpure.pure-web",
        redirect_uri: `https://${window.location.host}/app/`,
        response_type: "code id_token",
        response_mode: "web_message",
        scope: "email",
      })}`,
      "",
      "width=600,height=600",
    )
  }

  // @ts-ignore
  handleAppleAuthSuccess = (data) => {
    const { id_token, code } = data?.authorization
    const email = data?.user ? data?.user.email : null
    const host = this.isPureDating ? PURE_APP_DOMAIN : window.location.host

    window.location.href = `https://${host}/app/oauth?method=apple&code=${code}&id_token=${id_token}${
      email ? `&email=${email}` : ""
    }`
  }

  openGoogleAuth = () => {
    const testingHosts = [
      "pw-testing.stage-thepure.app",
      "pw-testing-2.stage-thepure.app",
    ]

    let clientId
    if (testingHosts.includes(window.location.host)) {
      clientId =
        "605973474333-ug6so42p83bcivbotuvp7bb10odtfd49.apps.googleusercontent.com"
    } else {
      clientId =
        "605973474333-g71ga2gjbaguk53hbu6on24vao2f7h9v.apps.googleusercontent.com"
    }

    const host = this.isPureDating ? PURE_APP_DOMAIN : window.location.host
    const redirectUrl = `https://${host}/app/onboarding`
    const scope = "profile email openid"
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUrl}&scope=${scope}&response_type=code&access_type=offline&include_granted_scopes=true`
  }

  @observable countryCode = ""

  getClientCountryCode = async (ctx: Context) => {
    if (this.countryCode) {
      return this.countryCode
    }
    const countryCode = await this.Providers.GlobalProvider.getClientCountryCode(
      ctx,
    )
    this.applyData({
      countryCode,
    })
    return countryCode
  }

  setClientCountryCode = (countryCode: string) => {
    if (this.countryCode) {
      return
    }

    this.applyData({ countryCode })
  }
}

export const PURE_DATING_DOMAIN = "pure.dating"
export const PURE_APP_DOMAIN = "pure.app"
export const PURE_APP_RU_DOMAIN = "pure-app.ru"
